import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { first, xor } from 'lodash';
import {
  Allocation,
  Availability,
  DealProcessingStatuses,
  DealStatus,
  PreservedAvailability,
} from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { BookingCode } from 'components/pages/Planner/hooks/usePlannerActions.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import { addToastNotification, clearNotification } from 'store/notification/reducer';
import {
  changeCurrentLineToLastLine,
  changeDealCurrentLineAvailabilityAssetsSot,
  changeDealCurrentLineData,
  changeDealData,
  changeDealLineAllocatedImpressions,
  changeFormParams,
  duplicateLine,
  removeTemporaryDealId,
  updatePlannerSettings,
} from 'store/dealManagement/reducer';
import { getDealDetails } from 'store/dealManagement/actions';
import { getIsProgrammaticDealLevelChange, getIsProgrammaticLineLevelChange } from 'store/dealManagement/selectors';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { cancelFunctions } from 'utils/cancelFunctions';
import {
  approveDeal,
  cancelDeal,
  checkAllocate,
  createDeal,
  createLine,
  deleteLine,
  editDeal,
  getAllocatedDeal,
  getDealProcessingStatus,
  rejectDeal,
  reserveDeal,
  stopLine,
  updateLine,
} from 'modules/api/programmaticDeal';
import { getIsReseller } from 'store/publisher/selectors';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import {
  resetCategoryFilters,
  resetServicesChanged,
  resetServicesSearchQuery,
  updateSelectedNonMediaServices,
} from 'store/nonMediaServices/reducer';
import { transformBookingStatusCode } from '../../common/transformPostData';
import { getDealData, getDealWithLineFormData, getLineFormData } from '../transformPostData';
import { combineProposalAllocationAndDealSummary, transformAvailability } from '../transformDealDetails';

interface ErrorMessage {
  lineId: string;
  message: string;
}
interface DealRequestResult {
  campaignID: string;
}

interface ProcessingStatusRequestResult extends DealRequestResult {
  messages: ErrorMessage[];
  dealIds?: string;
  processingStatus: DealProcessingStatuses;
}
interface LineRequestResult extends DealRequestResult {
  dealIds: string;
  dealId: string;
}

type DealRequestHandler<TResult> = () => Promise<TResult>;
type ProcessingStatusPollHandler<TResult> = (result: TResult) => void;
type CatchHandler = () => void;

interface UseDealAndLineActions {
  createLineHandler: () => Promise<void>;
  deleteLineHandler: () => Promise<void>;
  duplicateLineHandler: () => Promise<void>;
  stopLineHandler: () => Promise<void>;
  updateLineHandler: () => Promise<void>;
  checkAllocateHandler: () => Promise<void>;
  saveDeal: (bookingStatusCode: BookingCode, catchHandler?: CatchHandler) => void;
}
export const useDealAndLineActions = (): UseDealAndLineActions => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const commonDeal = useSelector((state: Store) => state.dealManagement.commonDeal);
  const programmatic = useSelector((state: Store) => state.dealManagement.programmatic);
  const dealId = useSelector(getDealId);
  const dealType = useSelector(getDealType);
  const backupDealSummary = useSelector((state: Store) => state.dealManagement.backupFormData.commonDeal.summary);
  const isNewDeal = useSelector(getIsNewDeal);
  const cpm = useSelector((state: Store) => state.dealManagement.commonDeal.cpm);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const lines = useSelector(getBackupLines);
  const currentLine = useSelector(getCurrentLine);
  const isCpmCampaignLevel = useSelector((state: Store) => state.dealManagement.isCpmCampaignLevel);
  const frontEndType = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frontEndType);

  const isDealLevelChange = useSelector(getIsProgrammaticDealLevelChange);
  const isLineLevelChange = useSelector(getIsProgrammaticLineLevelChange);
  const hasAdsDealLevelCPMEnabled = useHasFeatureAccess(FeatureFlags.DEAL_LEVEL_CPM);
  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);
  const isReseller = useSelector(getIsReseller);

  const updateDealDetails = async (campaignID: string): Promise<void> => {
    if (dealId) {
      await dispatch(
        getDealDetails(
          dealId,
          cancelFunctions,
          navigate,
          hasAdsDealLevelCPMEnabled,
          hasNonGuaranteedExtendedTargetEnabled,
        ),
      );
    } else if (campaignID) {
      navigate(`/deal/${campaignID}`, { replace: true });
    } else {
      navigate('/deals', { replace: true });
    }
  };

  const handleMessages = (messages: ErrorMessage[]): void => {
    const errors = messages.map(({ lineId, message }) => {
      const lineName = lines.find(({ id }) => id === lineId)?.name || currentLine.name;

      return `${lineName}: ${message}`;
    });

    dispatch(clearNotification());
    dispatch(addToastNotification({ description: errors.join('\n'), type: 'Failure' }));
  };

  const dealStatusComplete = async (result: ProcessingStatusRequestResult): Promise<void> => {
    dispatch(
      addToastNotification({
        description: `Campaign saved successfully - ${result.campaignID}`,
        type: 'Success',
        timeout: 2000,
      }),
    );

    dispatch(changeFormParams({ isForecastedAllocation: bookingStatusCode === DealStatus.PENDING_APPROVAL }));
    dispatch(changeFormParams({ hasUnsavedProposal: false }));
    updateDealDetails(result.campaignID);
  };

  const lineStatusComplete = async (result: LineRequestResult): Promise<void> => {
    dispatch(
      addToastNotification({
        description: `Success! Line ${currentLine.name} was saved`,
        type: 'Success',
        timeout: 2000,
      }),
    );

    dispatch(changeFormParams({ isForecastedAllocation: bookingStatusCode === DealStatus.PENDING_APPROVAL }));
    dispatch(changeFormParams({ hasUnsavedProposal: false }));
    dispatch(changeDealCurrentLineData({ isCurrentLineWithProposalAllocation: false }));
    updateDealDetails(result.dealId);
  };

  const handleDealRequest = async <TResult extends DealRequestResult>(
    requestHandler: DealRequestHandler<TResult>,
    pollCompleteHandler: ProcessingStatusPollHandler<TResult>,
    catchHandler: CatchHandler = () => undefined,
  ): Promise<void> => {
    dispatch(changeFormParams({ isEditingDisabled: true }));
    dispatch(
      addToastNotification({
        header: 'We are validating your solution',
        type: 'Info',
        timeout: 2000,
      }),
    );

    try {
      const result = await requestHandler();
      const poll = getDealProcessingStatus(result.campaignID || dealId, cancelFunctions);

      poll(
        ({
          data: { processingStatus, messages, campaignID },
        }: {
          data: { processingStatus: string; messages: ErrorMessage[]; campaignID: string };
        }) => {
          if (processingStatus === DealProcessingStatuses.ERROR) {
            if (messages) handleMessages(messages);
            updateDealDetails(campaignID);
            dispatch(changeFormParams({ isEditingDisabled: false }));
            return false;
          }

          if (processingStatus === DealProcessingStatuses.STILL_PROCESSING) {
            return true;
          }

          pollCompleteHandler(result);
          if (messages.length) handleMessages(messages);
          dispatch(changeFormParams({ isEditingDisabled: false }));
          return false;
        },
        1000,
      );
    } catch {
      catchHandler();
      dispatch(changeFormParams({ isEditingDisabled: false }));
    }

    dispatch(removeTemporaryDealId());
  };

  const approveDealHandlerRequest = async (): Promise<void> => {
    await handleDealRequest(() => approveDeal(cancelFunctions, { dealId }), dealStatusComplete);
  };

  const updateDealAndApprove = async (catchHandler: CatchHandler): Promise<void> => {
    const data = {
      ...getDealData({
        ...commonDeal,
        ...programmatic,
        cpm: isCpmCampaignLevel ? cpm : null,
        hasAdsDealLevelCPMEnabled,
      }),
      bookingStatusCode: undefined,
    };

    await handleDealRequest(() => editDeal(cancelFunctions, dealId, data), approveDealHandlerRequest, catchHandler);
  };

  const updateLineAndApprove = async (): Promise<void> => {
    const formData = getLineFormData(commonDeal, isCpmCampaignLevel, environmentId);
    const lineId =
      currentLine.id ??
      (await new Promise<string>((resolve) => {
        handleDealRequest<LineRequestResult>(
          () => createLine(cancelFunctions, formData, { dealId }, frontEndType),
          async (result) => {
            await lineStatusComplete(result);

            const lineIds = lines.map((line) => line.lineId);

            resolve(first(xor(result.dealIds, lineIds)) || '');
          },
        );
      }));

    await handleDealRequest<DealRequestResult>(
      () =>
        updateLine(
          cancelFunctions,
          formData,
          {
            dealId,
            lineId,
          },
          frontEndType,
        ),
      approveDealHandlerRequest,
    );
  };

  const submitNewDeal = async (bookingCode: BookingCode): Promise<void> => {
    const formData = getDealWithLineFormData({
      ...commonDeal,
      ...programmatic,
      cpm: isCpmCampaignLevel ? cpm : null,
      bookingStatusCode: transformBookingStatusCode(bookingCode),
      hasAdsDealLevelCPMEnabled,
      ...(!isReseller && { environmentId }),
    });
    await handleDealRequest(() => createDeal(cancelFunctions, formData), dealStatusComplete);
  };

  const approveDealHandler = async (catchHandler: CatchHandler): Promise<void> => {
    if (!isDealLevelChange && !isLineLevelChange) {
      await approveDealHandlerRequest();
    }

    if (!isDealLevelChange && isLineLevelChange) {
      await updateLineAndApprove();
    } else if (isDealLevelChange) {
      await updateDealAndApprove(catchHandler);
    }
  };

  const rejectDealHandler = async (): Promise<void> => {
    await handleDealRequest(() => rejectDeal(cancelFunctions, { dealId }), dealStatusComplete);
  };

  const cancelDealHandler = async (): Promise<void> => {
    await handleDealRequest(() => cancelDeal(cancelFunctions, { dealId }), dealStatusComplete);
  };

  const reserveDealHandler = (): Promise<void> =>
    handleDealRequest(() => reserveDeal(cancelFunctions, { dealId }), dealStatusComplete);

  const editDealHandler = async (catchHandler: CatchHandler): Promise<void> => {
    const data = {
      ...getDealData({
        ...commonDeal,
        ...programmatic,
        cpm: isCpmCampaignLevel ? cpm : null,
        hasAdsDealLevelCPMEnabled,
      }),
      bookingStatusCode: undefined,
    };
    await handleDealRequest(() => editDeal(cancelFunctions, dealId, data), dealStatusComplete, catchHandler);
  };

  const saveDeal = (bookingCode: BookingCode, catchHandler: CatchHandler = () => undefined): void => {
    if (!dealId) {
      submitNewDeal(bookingCode);
      return;
    }

    switch (bookingCode) {
      case DealStatus.PENDING_APPROVAL:
      case DealStatus.APPROVED:
        approveDealHandler(catchHandler);
        break;
      case DealStatus.REJECTED:
        rejectDealHandler();
        break;
      case DealStatus.TERMINATED:
      case DealStatus.CANCELLED:
        cancelDealHandler();
        break;
      case DealStatus.RESERVED:
        reserveDealHandler();
        break;
      default:
        editDealHandler(catchHandler);
    }
  };

  const duplicateLineHandler = async (): Promise<void> => {
    dispatch(duplicateLine(currentLine.id));
    dispatch(resetCategoryFilters());
    dispatch(resetServicesSearchQuery());
    dispatch(resetServicesChanged());
    dispatch(updateSelectedNonMediaServices({ selectedNonMediaServices: [] }));
  };

  const createLineHandler = async (): Promise<void> => {
    const formData = getLineFormData(commonDeal, isCpmCampaignLevel, environmentId);

    await handleDealRequest(() => createLine(cancelFunctions, formData, { dealId }, frontEndType), lineStatusComplete);
  };

  const updateLineHandler = async (): Promise<void> => {
    const formData = getLineFormData(commonDeal, isCpmCampaignLevel, environmentId);

    await handleDealRequest(
      () =>
        updateLine(
          cancelFunctions,
          formData,
          {
            dealId,
            lineId: currentLine.id,
          },
          frontEndType,
        ),
      lineStatusComplete,
    );

    dispatch(updatePlannerSettings({ isClientSideAssetFiltering: false }));
  };

  const stopLineHandler = async (): Promise<void> => {
    await handleDealRequest(() => stopLine(cancelFunctions, { dealId, lineId: currentLine.id }), lineStatusComplete);
  };

  const deleteLineHandler = async (): Promise<void> => {
    if (!currentLine.lineId) {
      dispatch(changeCurrentLineToLastLine());
      return;
    }

    await handleDealRequest(() => deleteLine(cancelFunctions, { dealId, lineId: currentLine.id }), lineStatusComplete);
  };

  const checkAllocatePollComplete = async ({ campaignID }: Allocation): Promise<void> => {
    try {
      const currentLineAllocationProposal = await getAllocatedDeal(campaignID, cancelFunctions, dealType, lines);

      const summary = combineProposalAllocationAndDealSummary(
        isNewDeal,
        !currentLine.lineId,
        currentLine.preservedAllocation,
        currentLineAllocationProposal,
        commonDeal.summary,
        backupDealSummary,
        dealType,
        hasNonGuaranteedExtendedTargetEnabled,
      );

      dispatch(
        changeDealLineAllocatedImpressions(
          transformAvailability(currentLineAllocationProposal) as Availability & PreservedAvailability,
        ),
      );

      dispatch(
        changeDealData({
          summary,
        }),
      );
      dispatch(changeFormParams({ temporaryDealId: campaignID }));

      let adjustedSot = 0;
      if (currentLineAllocationProposal?.summary?.adjustedSots?.length === 1) {
        [adjustedSot] = currentLineAllocationProposal.summary.adjustedSots;
      }

      dispatch(
        changeDealCurrentLineData({
          deliveredImpressions: currentLineAllocationProposal.deliveredImpressions,
          isCurrentLineWithProposalAllocation: true,
          adjustedSot,
        }),
      );

      dispatch(
        changeDealCurrentLineAvailabilityAssetsSot(
          summary.availability.assets.map((asset) => ({
            frameId: asset.frameId,
            sot: asset.sot,
          })),
        ),
      );

      dispatch(
        addToastNotification({
          header: 'The calculated Qty is not yet reserved. Click Send for approval/Confirm/Save to proceed',
          type: 'Info',
          timeout: 10000,
        }),
      );

      dispatch(changeFormParams({ isForecastedAllocation: true }));
      dispatch(changeFormParams({ hasUnsavedProposal: true }));
    } catch {
      dispatch(clearNotification());
    }

    dispatch(changeFormParams({ isEditingDisabled: false }));
  };

  const checkAllocateHandler = async (): Promise<void> => {
    const formData = getDealWithLineFormData({
      ...commonDeal,
      ...programmatic,
      cpm: isCpmCampaignLevel ? cpm : null,
      bookingStatusCode: DealStatus.PROPOSAL,
      hasAdsDealLevelCPMEnabled,
      ...(!isReseller && { environmentId }),
      ...(dealId ? { overiddenCampaignId: dealId } : { overiddenCampaignId: null }),
    });

    await handleDealRequest(() => checkAllocate(cancelFunctions, formData), checkAllocatePollComplete);
  };

  return {
    createLineHandler,
    deleteLineHandler,
    duplicateLineHandler,
    stopLineHandler,
    updateLineHandler,
    checkAllocateHandler,
    saveDeal,
  };
};
